import { FormEvent, useLayoutEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { P, PrimaryAction } from '@ohgoodlord/design-system'
import { track } from '../../../../../common/utils'
import { useStore } from '../../../../hooks/use-store'
import { Model } from '../../../../types/model'
import { OverlayComponentProps } from '../../types'
import { DefaultFooter } from '../default-footer'
import { DefaultHeader } from '../default-header'

export const TERMS_AND_CONDITIONS = 'terms-and-conditions' as const

export interface TermsAndConditionsOverlayModel
  extends Model<typeof TERMS_AND_CONDITIONS> {
  url: string
  privacyPolicy: string
  termsAndConditions: string
  options?: {
    disableEditActionOnSubmit?: boolean
  }
}

export const TERMS_OVERLAY_TITLE = 'Declaration'
const ACCEPTANCE_MESSAGE = 'I have read and accept the declaration  📝'

// The declaration page stretches to the iframe, so the frame has to match the
// wrapped text or a phone clips it. Narrower copy runs longer.
export const declarationFrameHeight = (width: number) => {
  const narrow = 900000 / width - 560
  const wide = 540000 / width + 440

  return Math.ceil(Math.max(narrow, wide, 0))
}

export const TermsAndConditionsOverlay = ({
  model,
  isMobile,
  onSendEvent,
  onSendMessage,
  onOverlaySubmit,
  onClose,
  renderOverlayHeader,
}: OverlayComponentProps<TermsAndConditionsOverlayModel>) => {
  const { disableEditActions } = useStore()
  const frameSlotRef = useRef<HTMLDivElement>(null)
  const [frameHeight, setFrameHeight] = useState('100%')

  useLayoutEffect(() => {
    const slot = frameSlotRef.current

    if (slot === null) {
      return
    }

    const fitFrame = () => {
      const width = slot.clientWidth

      if (width <= 0) {
        return
      }

      const height = Math.max(declarationFrameHeight(width), slot.clientHeight)
      setFrameHeight(`${height}px`)
    }

    fitFrame()
    const observer = new ResizeObserver(fitFrame)
    observer.observe(slot)

    return () => observer.disconnect()
  }, [])

  const handleAccept = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    track('Click - Accepted mandatory terms', {})

    if (model.options?.disableEditActionOnSubmit === true) {
      disableEditActions()
    }

    onSendMessage({ message: ACCEPTANCE_MESSAGE })
    onSendEvent({
      name: 'form',
      value: {
        isAccepted: 'true',
        declarationAcceptedUrl: model.url,
      },
    })
    onOverlaySubmit()
  }

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
      `}
      onSubmit={handleAccept}
    >
      <div
        css={css`
          flex-shrink: 0;
        `}
      >
        {renderOverlayHeader !== undefined ? (
          renderOverlayHeader()
        ) : (
          <DefaultHeader
            modal={model.modal}
            isMobile={isMobile}
            title={TERMS_OVERLAY_TITLE}
            onClose={onClose}
          />
        )}
      </div>
      <div
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;
          gap: 15px;
          min-height: 0;
          padding: 15px;
        `}
      >
        <P>By clicking accept you agree to the declaration below.</P>
        <div
          ref={frameSlotRef}
          css={css`
            flex: 1;
            min-height: 0;
            overflow: auto;
          `}
        >
          <iframe
            src={model.url}
            aria-label="referencing-declaration"
            scrolling="no"
            css={css`
              display: block;
              width: 100%;
              height: ${frameHeight};
              border: none;
            `}
          />
        </div>
      </div>
      <div
        css={css`
          flex-shrink: 0;
        `}
      >
        <DefaultFooter
          renderActions={() => (
            <PrimaryAction type="submit" isFullWidth testId="modal-primary-action-button">
              Accept
            </PrimaryAction>
          )}
        />
      </div>
    </form>
  )
}
